import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { 
  PlusIcon,
  CurrencyPoundIcon,
  ChartBarIcon,
  ArrowUpIcon,
  EyeIcon,
  TrashIcon,
  DocumentTextIcon,
  StarIcon,
  ShoppingCartIcon,
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
  CreditCardIcon,
  ExclamationTriangleIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';
import { formatCurrency } from '../utils';
import { useAuth } from '../context/AuthContext';
import { AddProductModal } from '../components/modals/AddProductModal';
import { productService, Product } from '../services/productService';
import { sellerOnboardingService, StripeAccountStatus } from '../services/sellerOnboardingService';
import { useNavigate } from 'react-router-dom';
export const SellPage: React.FC = () => {
  const navigate = useNavigate();
  const [isAddProductModalOpen, setIsAddProductModalOpen] = useState(false);
  const [myProducts, setMyProducts] = useState<Product[]>([]);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [stripeAccountStatus, setStripeAccountStatus] = useState<StripeAccountStatus | null>(null);
  const [stats, setStats] = useState({
    totalProducts: 0,
    approvedProducts: 0,
    pendingProducts: 0,
    totalEarnings: 0,
    totalSales: 0,
  });
  const { user } = useAuth();

  useEffect(() => {
    loadMyProducts();
    if (user?.role === 'SELLER') {
      loadStripeAccountStatus();
    }
  }, [user]);

  const loadStripeAccountStatus = async () => {
    try {
      const status = await sellerOnboardingService.getStripeAccountStatus();
      setStripeAccountStatus(status);
    } catch (error) {
      console.error('Error loading Stripe account status:', error);
    }
  };

  const loadMyProducts = async () => {
    setIsLoadingProducts(true);
    try {
      const products = await productService.getMyProducts();
      setMyProducts(products);
      
      // Calculate stats
      const approved = products.filter(p => p.status === 'APPROVED').length;
      const pending = products.filter(p => p.status === 'PENDING').length;
      const totalEarnings = products
        .filter(p => p.status === 'APPROVED')
        .reduce((sum, p) => sum + (p.downloads || 0) * parseFloat(p.price), 0);
      
      setStats({
        totalProducts: products.length,
        approvedProducts: approved,
        pendingProducts: pending,
        totalEarnings,
        totalSales: products.reduce((sum, p) => sum + (p.downloads || 0), 0),
      });
    } catch (error) {
      console.error('Error loading products:', error);
    } finally {
      setIsLoadingProducts(false);
    }
  };

  const handleDelete = async (productId: string) => {
    if (window.confirm('Are you sure you want to delete this product? This action cannot be undone.')) {
      try {
        await productService.deleteProduct(productId);
        setMyProducts(myProducts.filter(p => p.id !== productId));
        loadMyProducts(); // Refresh stats
      } catch (error) {
        console.error('Error deleting product:', error);
        alert('Failed to delete product. Please try again.');
      }
    }
  };

  const getImageUrl = (product: Product): string => {
    if (product.thumbnailUrl) return product.thumbnailUrl;
    if (product.thumbnail) {
      const thumbnailMedia = product.media?.find(m => m.entityType === 'PRODUCT_THUMBNAIL');
      if (thumbnailMedia?.signedUrl) return thumbnailMedia.signedUrl;
      if (thumbnailMedia?.fileUrl) return thumbnailMedia.fileUrl;
    }
    const firstMedia = product.media?.[0];
    if (firstMedia?.signedUrl) return firstMedia.signedUrl;
    if (firstMedia?.fileUrl) return firstMedia.fileUrl;
    return 'https://via.placeholder.com/400x300?text=No+Image';
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'APPROVED':
        return (
          <Badge variant="success" className="flex items-center">
            <CheckCircleIcon className="h-3 w-3 mr-1" />
            Approved
          </Badge>
        );
      case 'PENDING':
        return (
          <Badge variant="warning" className="flex items-center">
            <ClockIcon className="h-3 w-3 mr-1" />
            Pending Review
          </Badge>
        );
      case 'REJECTED':
        return (
          <Badge variant="error" className="flex items-center">
            <XCircleIcon className="h-3 w-3 mr-1" />
            Rejected
          </Badge>
        );
      case 'SUSPENDED':
        return (
          <Badge variant="secondary" className="flex items-center">
            <XCircleIcon className="h-3 w-3 mr-1" />
            Suspended
          </Badge>
        );
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-secondary-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stripe Setup Banner */}
        {user?.role === 'SELLER' && stripeAccountStatus && 
         (!stripeAccountStatus.accountId || stripeAccountStatus.status !== 'ENABLED' || !stripeAccountStatus.onboardingCompleted) && (
          <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start">
              <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 mt-0.5 mr-3 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="text-sm font-medium text-yellow-800 mb-1">
                  Payment Setup Required
                </h3>
                <p className="text-sm text-yellow-700 mb-3">
                  To receive payments from your product sales, you need to link your Stripe Connect account. 
                  You'll receive 90% of each sale, with 10% going to the platform.
                </p>
                <Button
                  size="sm"
                  onClick={() => navigate('/seller/onboarding')}
                  className="bg-yellow-600 hover:bg-yellow-700 text-white"
                >
                  <CreditCardIcon className="h-4 w-4 mr-2" />
                  Set Up Stripe Account
                  <ArrowRightIcon className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Header Section */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-4xl font-bold text-secondary-900 mb-2">
                Sell Your Products
              </h1>
              <p className="text-lg text-secondary-600">
                Upload and manage your care documents to reach thousands of professionals
              </p>
            </div>
            <Button
              size="lg"
              onClick={() => setIsAddProductModalOpen(true)}
              className="flex items-center"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              Add New Product
            </Button>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center">
                  <div className="p-3 bg-primary-100 rounded-lg">
                    <DocumentTextIcon className="h-6 w-6 text-primary-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-secondary-600">Total Products</p>
                    <p className="text-2xl font-bold text-secondary-900">{stats.totalProducts}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center">
                  <div className="p-3 bg-green-100 rounded-lg">
                    <CheckCircleIcon className="h-6 w-6 text-green-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-secondary-600">Approved</p>
                    <p className="text-2xl font-bold text-secondary-900">{stats.approvedProducts}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center">
                  <div className="p-3 bg-yellow-100 rounded-lg">
                    <ClockIcon className="h-6 w-6 text-yellow-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-secondary-600">Pending</p>
                    <p className="text-2xl font-bold text-secondary-900">{stats.pendingProducts}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center">
                  <div className="p-3 bg-blue-100 rounded-lg">
                    <ShoppingCartIcon className="h-6 w-6 text-blue-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-secondary-600">Total Sales</p>
                    <p className="text-2xl font-bold text-secondary-900">{stats.totalSales}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center">
                  <div className="p-3 bg-purple-100 rounded-lg">
                    <CurrencyPoundIcon className="h-6 w-6 text-purple-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-secondary-600">Total Earnings</p>
                    <p className="text-2xl font-bold text-secondary-900">
                      {formatCurrency(stats.totalEarnings)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Products Section */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-secondary-900">My Products</h2>
            <div className="flex items-center space-x-3">
              <Button
                variant="outline"
                onClick={() => setIsAddProductModalOpen(true)}
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                Add Product
              </Button>
              <Link to="/dashboard">
                <Button variant="outline">
                  <ChartBarIcon className="h-4 w-4 mr-2" />
                  View Dashboard
                </Button>
              </Link>
            </div>
          </div>

          {isLoadingProducts ? (
            <div className="flex items-center justify-center py-20">
              <div className="text-center">
                <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary-600 mx-auto"></div>
                <p className="mt-4 text-secondary-600 text-lg">Loading your products...</p>
              </div>
            </div>
          ) : myProducts.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <div className="max-w-md mx-auto">
                  <div className="mb-6">
                    <div className="mx-auto w-24 h-24 bg-primary-100 rounded-full flex items-center justify-center">
                      <DocumentTextIcon className="h-12 w-12 text-primary-600" />
                    </div>
                  </div>
                  <h3 className="text-2xl font-bold text-secondary-900 mb-3">
                    Start Selling Today
                  </h3>
                  <p className="text-secondary-600 mb-8">
                    Upload your first product and start earning. Share your expertise with care professionals across the UK.
                  </p>
                  <Button
                    size="lg"
                    onClick={() => setIsAddProductModalOpen(true)}
                    className="mx-auto"
                  >
                    <PlusIcon className="h-5 w-5 mr-2" />
                    Upload Your First Product
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {myProducts.map((product) => (
                <Card key={product.id} hover className="group relative overflow-hidden">
                  {/* Image Container */}
                  <div className="relative aspect-video bg-gradient-to-br from-primary-50 to-primary-100 overflow-hidden">
                    <img
                      src={getImageUrl(product)}
                      alt={product.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = 'https://via.placeholder.com/400x300?text=No+Image';
                      }}
                    />
                    
                    {/* Status Badge */}
                    <div className="absolute top-3 left-3">
                      {getStatusBadge(product.status)}
                    </div>

                    {/* Actions Overlay */}
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-200 flex items-center justify-center">
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex space-x-2">
                        <Link to={`/product/${product.id}`}>
                          <button className="p-2 bg-white rounded-lg shadow-md hover:bg-primary-50 transition-colors">
                            <EyeIcon className="h-5 w-5 text-primary-600" />
                          </button>
                        </Link>
                        <button
                          onClick={() => handleDelete(product.id)}
                          className="p-2 bg-white rounded-lg shadow-md hover:bg-red-50 transition-colors"
                        >
                          <TrashIcon className="h-5 w-5 text-red-600" />
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Content */}
                  <CardContent className="p-5">
                    <h3 className="font-semibold text-secondary-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                      {product.title}
                    </h3>
                    
                    <div className="flex items-center justify-between mt-4">
                      <div>
                        <p className="text-xl font-bold text-primary-600">
                          {formatCurrency(parseFloat(product.price))}
                        </p>
                        <div className="flex items-center mt-1 space-x-3 text-xs text-secondary-500">
                          <span className="flex items-center">
                            <ArrowUpIcon className="h-3 w-3 mr-1" />
                            {product.downloads || 0} downloads
                          </span>
                          <span className="flex items-center">
                            <StarIcon className="h-3 w-3 mr-1 text-yellow-400 fill-current" />
                            {product.rating > 0 ? product.rating.toFixed(1) : '0.0'}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Footer */}
                    <div className="mt-4 pt-4 border-t border-secondary-100 flex items-center justify-between text-xs text-secondary-500">
                      <span>
                        {new Date(product.createdAt).toLocaleDateString()}
                      </span>
                      <Link to={`/product/${product.id}`} className="text-primary-600 hover:text-primary-700 font-medium">
                        View Details →
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Help Section */}
        <Card className="bg-gradient-to-r from-primary-50 to-secondary-50 border-primary-200">
          <CardContent className="p-8">
            <div className="flex items-start space-x-6">
              <div className="flex-shrink-0">
                <div className="w-16 h-16 bg-primary-600 rounded-full flex items-center justify-center">
                  <DocumentTextIcon className="h-8 w-8 text-white" />
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-secondary-900 mb-2">
                  Tips for Successful Selling
                </h3>
                <ul className="space-y-2 text-secondary-700">
                  <li className="flex items-start">
                    <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Use clear, descriptive titles and detailed descriptions</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Add high-quality thumbnails and preview images</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Price competitively based on market research</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Use relevant tags to improve discoverability</span>
                  </li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Add Product Modal */}
        <AddProductModal
          isOpen={isAddProductModalOpen}
          onClose={() => setIsAddProductModalOpen(false)}
          onSuccess={() => {
            loadMyProducts();
            alert('Product created successfully! It will be reviewed before being published.');
          }}
        />
      </div>
  );
};

